import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isSuperAdmin } from '@foundry/shared';
import { useMe } from '../../api/auth.js';
import { useClearSecret, useSecrets, useSetSecret } from '../../api/secrets.js';
import type { SecretStatus } from '../../api/types.js';
import { Alert, Button, Card, Spinner } from '../../components/ui.js';
import { useConfirm } from '../../components/ConfirmDialog.js';

/** Super-admin management of integration credentials (Anthropic, GitHub, AWS, …). */
export function SecretsPage() {
  const { data: user } = useMe();
  const q = useSecrets();

  if (user && !isSuperAdmin(user.role)) return <Navigate to="/console" replace />;
  if (q.isPending) return <Spinner />;
  if (q.isError || !q.data) return <Alert>Could not load keys.</Alert>;

  const { configured, secrets } = q.data;
  const groups = [...new Set(secrets.map((s) => s.group))];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Keys &amp; secrets</h1>
        <p className="opacity-70">
          The credentials the platform uses. Values are encrypted at rest and never shown again once
          saved — only whether each is set, and its last few characters.
        </p>
      </div>

      {!configured ? (
        <div className="rounded-md bg-amber-100 p-4 text-sm text-amber-900">
          <p className="font-medium">Secret storage isn’t configured yet.</p>
          <p className="mt-1">
            Set a <code className="font-mono">SECRETS_ENCRYPTION_KEY</code> (16+ characters) in the
            API’s environment to enable saving keys here. Until then the fields below are read-only.
          </p>
        </div>
      ) : null}

      {groups.map((group) => (
        <Card key={group}>
          <h2 className="mb-3 font-title text-2xl tracking-wide">{group}</h2>
          <div className="flex flex-col gap-4">
            {secrets
              .filter((s) => s.group === group)
              .map((s) => (
                <SecretRow key={s.key} secret={s} disabled={!configured} />
              ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function SecretRow({ secret, disabled }: { secret: SecretStatus; disabled: boolean }) {
  const set = useSetSecret();
  const clear = useClearSecret();
  const confirm = useConfirm();
  const [value, setValue] = useState('');

  const onSave = (): void => {
    if (!value.trim()) return;
    set.mutate({ key: secret.key, value: value.trim() }, { onSuccess: () => setValue('') });
  };

  return (
    <div className="rounded-md bg-neutral/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{secret.label}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            secret.active ? 'bg-primary/15 text-primary' : 'bg-secondary/10 opacity-70'
          }`}
        >
          {secret.active ? 'In use' : 'Stored for later'}
        </span>
        <span className="ml-auto text-sm">
          {secret.isSet ? (
            <span className="opacity-70">
              Set · <span className="font-mono">{secret.preview}</span>
            </span>
          ) : (
            <span className="opacity-50">Not set</span>
          )}
        </span>
      </div>
      <p className="mt-1 text-xs opacity-60">{secret.description}</p>
      {secret.isSet && secret.updatedByEmail ? (
        <p className="mt-0.5 text-xs opacity-40">
          Updated by {secret.updatedByEmail}
          {secret.updatedAt ? ` · ${new Date(secret.updatedAt).toLocaleDateString()}` : ''}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type={secret.secret ? 'password' : 'text'}
          autoComplete="off"
          placeholder={secret.isSet ? 'Enter a new value to replace it' : 'Enter value'}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          className="min-w-64 flex-1 rounded-md border border-secondary/20 bg-white px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-40"
        />
        <Button disabled={disabled || set.isPending || !value.trim()} onClick={onSave}>
          {set.isPending ? 'Saving…' : secret.isSet ? 'Replace' : 'Save'}
        </Button>
        {secret.isSet ? (
          <Button
            variant="secondary"
            disabled={disabled || clear.isPending}
            onClick={() =>
              void confirm({
                title: 'Clear secret',
                message: `Clear ${secret.label}?`,
                confirmLabel: 'Clear',
                danger: true,
              }).then((ok) => {
                if (ok) clear.mutate(secret.key);
              })
            }
          >
            Clear
          </Button>
        ) : null}
      </div>
      {set.isError ? (
        <div className="mt-2">
          <Alert>Could not save the value.</Alert>
        </div>
      ) : null}
    </div>
  );
}
